import { Divider, Typography } from '@mui/material';
import { LP_INVESTMENT_LIST } from 'adminConstants/routes';
import AllocationsPreview from 'components/new/AllocationsPreview';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { AllocationRuleLabels, FundingSourceLabels } from 'further-ui/labels';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient';
import React from 'react';
import { useParams } from 'react-router-dom';

type Props = { investmentId: string; vehicleId: string };

const ViewInvestment: React.FC<Props> = ({ investmentId, vehicleId }) => {
  const { vehicle } = useLayoutContext();

  const [investment] = api.lpInvestment.get.useSuspenseQuery({
    investmentId,
    vehicleId,
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  const holdingClassRows = investment.holdingClasses.map((holdingClass) => ({
    id: holdingClass.holdingClassId,
    ...holdingClass,
  }));

  const holdingClassColumns: Array<
    TableColumn<(typeof holdingClassRows)[number]>
  > = [
    {
      label: 'Holding class',
      key: 'name',
    },
    {
      label: 'Number of units',
      key: 'numberOfUnits',
      render: (row) => numberToDisplayString(row.numberOfUnits),
    },
    {
      label: 'Investment amount',
      key: 'investmentAmountTcy',
      render: (row) =>
        numberToCurrencyString(row.investmentAmountTcy, {
          currency: row.currency,
        }),
    },
    {
      label: 'Unit price',
      key: 'unitPriceTcy',
      render: (row) =>
        numberToCurrencyString(row.unitPriceTcy, {
          currency: row.currency,
          unlimitedDp: true,
        }),
    },
  ];

  return (
    <Page
      tabTitle="Investments: View Investments"
      heading="View investment"
      spacing="lg"
      goBackAction={LP_INVESTMENT_LIST}
      withHeadingDivider
    >
      <Column spacing="lg">
        <StaticField label="Vehicle">{vehicle?.vehicleName}</StaticField>
        <StaticField label="Asset">
          {investment.asset?.name || 'N/A'}
        </StaticField>
        <StaticField label="Deal name">{investment.dealName}</StaticField>
        <StaticField label="Deal completion date">
          {investment.dealCompletionDate?.toReadable() ?? 'N/A'}
        </StaticField>

        <StaticField label="Total investment amount">
          {numberToCurrencyString(investment.totalInvestmentAmountTcy, {
            currency: investment.currencyCode,
          })}
        </StaticField>
        <StaticField label="Units">
          {numberToDisplayString(investment.units)}
        </StaticField>
        <StaticField label="Average unit price">
          {numberToCurrencyString(investment.averageUnitPriceTcy, {
            currency: investment.currencyCode,
          })}
        </StaticField>
        <StaticField label="Allocation rule">
          {investment.allocationRule
            ? AllocationRuleLabels[investment.allocationRule]
            : 'N/A'}
        </StaticField>
        <StaticField label="Funding Source">
          {FundingSourceLabels[investment.fundingSource]}
        </StaticField>
      </Column>

      {investment.holdingClasses.length > 0 && (
        <>
          <Divider />
          <Typography variant="h5">Investment per holding class</Typography>
          <Table columns={holdingClassColumns} rows={holdingClassRows} />
        </>
      )}
      <Divider />
      <Typography variant="h5">Allocations</Typography>
      <AllocationsPreview
        vehicleId={investment.vehicleId}
        allocatedActionLabel="Investment"
        currency={investment.currencyCode}
        baseCurrency={vehicle?.baseCurrency ?? investment.currencyCode}
        allocations={investment.allocations ?? []}
        date={investment.dealCompletionDate}
        currentActionIds={[investment.id]}
      />
      {vehicle && (
        <>
          <Divider />
          <Typography variant="h5">Account transactions</Typography>
          <TransactionAllocator
            transactionTypeOptions={transactionTypeOptions}
            transactions={investment.transactions}
            transactionCurrency={investment.currencyCode}
            fxRate={investment.currencyRate ?? null}
            vehicle={vehicle}
            readOnly={true}
            showCurrencyRate={false}
          />
        </>
      )}
    </Page>
  );
};

const ViewInvestmentWrapper: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { selectedVehicleId } = useLayoutContext();

  if (!id || !selectedVehicleId) {
    return null;
  }

  return <ViewInvestment investmentId={id} vehicleId={selectedVehicleId} />;
};
export default ViewInvestmentWrapper;
